import {useTranslation} from 'react-i18next';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {Box, Grid, IconButton, TextField, useTheme} from '@mui/material';

import {EmpionButton} from '../../../../../components/EmpionButton/EmpionButton';
import {getFieldStyle} from '../../../../../layouts/theme/FieldStyles';
import type {WorkforceProfileExtractedWorkExperience} from '../../types/workforceProfileDetailsTypes';

export interface Props {
  rows: WorkforceProfileExtractedWorkExperience[];
  onChange: (rows: WorkforceProfileExtractedWorkExperience[]) => void;
}

export const WorkforceProfileWizardExperienceEditor = ({rows, onChange}: Props) => {
  const theme = useTheme();
  const {t} = useTranslation('translation', {keyPrefix: 'workforceProfilesPage.wizard.experienceEditor'});

  const updateRow = (index: number, patch: Partial<WorkforceProfileExtractedWorkExperience>) => {
    onChange(rows.map((row, rowIndex) => (rowIndex === index ? {...row, ...patch} : row)));
  };

  const addRow = () => {
    onChange([...rows, {roleTitle: '', organizationDescriptor: '', duration: '', highlights: []}]);
  };

  const removeRow = (index: number) => {
    onChange(rows.filter((_, rowIndex) => rowIndex !== index));
  };

  const normalizeHighlights = (highlights: string[]) =>
    highlights.map((line) => line.trim()).filter(Boolean);

  const updateHighlights = (index: number, value: string) => {
    updateRow(index, {
      highlights: value === '' ? [] : value.split('\n'),
    });
  };

  return (
    <Box>
      {rows.map((row, index) => (
        <Box key={`experience-${index}`} sx={{mb: 2.5, pb: 2, borderBottom: `1px solid ${theme.palette.divider}`}}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size='small'
                label={t('roleTitle')}
                value={row.roleTitle}
                onChange={(event) => updateRow(index, {roleTitle: event.target.value})}
                sx={getFieldStyle(theme)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size='small'
                label={t('organization')}
                value={row.organizationDescriptor}
                onChange={(event) => updateRow(index, {organizationDescriptor: event.target.value})}
                sx={getFieldStyle(theme)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size='small'
                label={t('duration')}
                value={row.duration}
                onChange={(event) => updateRow(index, {duration: event.target.value})}
                sx={getFieldStyle(theme)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                size='small'
                label={t('highlights')}
                value={row.highlights.join('\n')}
                onChange={(event) => updateHighlights(index, event.target.value)}
                onBlur={() => updateRow(index, {highlights: normalizeHighlights(row.highlights)})}
                helperText={t('highlightsHint')}
                sx={getFieldStyle(theme)}
              />
            </Grid>
          </Grid>
          <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 1}}>
            <IconButton aria-label={String(t('remove'))} onClick={() => removeRow(index)} size='small'>
              <DeleteOutlineIcon fontSize='small' />
            </IconButton>
          </Box>
        </Box>
      ))}
      <EmpionButton variant='secondary' startIcon={<AddIcon />} onClick={addRow}>
        {t('add')}
      </EmpionButton>
    </Box>
  );
};
